<template>
	<div
		class="amelipro-text-field__wrapper"
		:class="fieldClasses"
		:style="globalFieldStyles"
	>
		<div
			class="d-inline-flex align-start"
			:class="labelClasses"
		>
			<label
				class="mr-2 amelipro-text-field__label"
				:for="id"
				:style="labelStyles"
			>
				{{ label }}

				<span v-if="ariaRequired">
					<span aria-hidden="true">
						&nbsp;*
					</span>

					<span class="d-sr-only">
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<slot name="labelInfo" />
		</div>

		<VTextField
			:id="id"
			ref="inputTextField"
			v-model="inputValue"
			:aria-describedby="describedby"
			:aria-invalid="!valid"
			:aria-required="ariaRequired"
			:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
			class="pt-0 amelipro-text-field"
			:clearable="clearable"
			color="ap-blue darken-1"
			:counter="counter"
			dense
			:disabled="disabled"
			:hide-details="hideErrorMessage"
			:max="maxDate"
			:min="minDate"
			outlined
			:placeholder="placeholder"
			:readonly="readonly"
			:rules="inputRules"
			:style="inputStyles"
			:type="type"
			v-bind="$attrs"
			v-on="$listeners"
			@blur="focused = false"
			@change="emitChangeEvent"
			@focus="focused = true"
		>
			<template #append>
				<slot name="append" />
			</template>

			<template #append-outer>
				<slot name="append-outer" />
			</template>

			<template #message="{ message }">
				<AmeliproMessage
					:id="errorId"
					no-icon
					text
					type="error"
				>
					<p class="mb-0">
						{{ message }}
					</p>
				</AmeliproMessage>
			</template>
		</VTextField>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref } from 'vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IndexedObject } from '../../types';
	import { InputTextField } from './types';
	import { VTextField } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { notAfterDate } from '../../rules/notAfterDate';
	import { notBeforeDate } from '../../rules/notBeforeDate';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},

		/**
		 * @deprecated N'est plus utilisé dans le code
		 *
		 */
		bgWhite: {
			type: Boolean,
			default: false,
		},
		clearable: {
			type: Boolean,
			default: false,
		},
		counter: {
			type: [Boolean, Number, String] as PropType<boolean | number>,
			default: undefined,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		globalMaxWidth: {
			type: String,
			default: undefined,
		},
		globalMinWidth: {
			type: String,
			default: undefined,
		},
		globalWidth: {
			type: String,
			default: undefined,
		},
		hideErrorMessage: {
			type: [String, Boolean] as PropType<string | boolean>,
			default: false,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		inputMaxWidth: {
			type: String,
			default: undefined,
		},
		inputMinWidth: {
			type: String,
			default: undefined,
		},
		label: {
			type: String,
			required: true,
		},
		labelMaxWidth: {
			type: String,
			default: undefined,
		},
		labelMinWidth: {
			type: String,
			default: undefined,
		},
		maxDate: {
			type: String,
			default: undefined,
		},
		minDate: {
			type: String,
			default: undefined,
		},
		placeholder: {
			type: String,
			default: undefined,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		type: {
			type: String,
			default: 'text',
		},
		value: {
			type: [String, Number] as PropType<string | number>,
			default: undefined,
		},
	});

	// VTextField ref
	const inputTextField = ref<InputTextField>({ valid: true, hasMessages: false });

	// v-model
	const inputValue = computed({
		get: (): string | number | null => props.value,
		set: (newValue: string | number | null): void => {
			emit('change', newValue, props.id);
		},
	});

	const valid = computed(() => Boolean(inputTextField.value?.valid));
	const focused = ref(false);
	const errorId = computed<string>(() => `${props.id}-error`);
	const describedby = computed<string | undefined>(() => (valid.value ? undefined : errorId.value));
	const displayError = computed(() => !inputTextField.value.valid && inputTextField.value.hasMessages);
	const inputRules = computed<ValidationRule[]>(() => {
		//const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		if (props.type === 'date') {

			if (props.maxDate) {
				rules.push(notAfterDate(props.maxDate) as ValidationRule);
			}

			if (props.minDate) {
				rules.push(notBeforeDate(props.minDate) as ValidationRule);
			}
		}

		return rules;
	});

	const emit = defineEmits(['change']);
	const emitChangeEvent = (): void => {
		emit('change', inputValue.value, props.id);
	};

	const fieldClasses = computed<string>(() => {
		const classes = ['d-flex', 'flex-column'];

		if (props.horizontal) {
			classes.push('flex-md-row');
		}

		return classes.join(' ');
	});

	const labelClasses = computed<string>(() => {
		let classes = '';

		if (props.horizontal) {
			classes = 'mt-md-2 mr-md-2';
		}

		return classes;
	});

	const globalFieldStyles = computed<IndexedObject>(() => ({
		maxWidth: props.globalMaxWidth,
		minWidth: props.globalMinWidth,
		width: props.globalWidth,
	}));

	const inputStyles = computed<IndexedObject>(() => ({
		maxWidth: props.inputMaxWidth,
		minWidth: props.inputMinWidth,
	}));

	const labelStyles = computed<IndexedObject>(() => ({
		maxWidth: props.labelMaxWidth,
		minWidth: props.labelMinWidth,
	}));

	const inputBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (focused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});
</script>

<style lang="scss" scoped>
	.amelipro-text-field {
		:deep(.v-input__slot) {
			min-height: $input-min-height !important;
			border: v-bind(inputBorderStyle);
			border-radius: $input-radius;

			& input {
				max-height: unset;
			}

			fieldset {
				border: 0;
			}
		}
	}

	.amelipro-text-field__label {
		margin-bottom: 4px;
		font-weight: $label-font-weight;
	}
</style>
